import React, { useCallback, useEffect, useState } from 'react';
import {
  API_ADD_LOAN_DISBURSE_MULTIPLE,
  API_BRANCHES,
  API_CENTERS,
  API_EMPLOYEES,
  API_GET_LOANS,
  API_DISBURSEMENT_CLIENT_HISTORY,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { confirmAction } from '../utils/confirmAction';
import { apiJson } from '../utils/apiClient';

const createRow = () => ({
  branch_id: '',
  center_id: '',
  client_id: '',
  date_of_disbursement: '',
  date_of_repayment: '',
  disbursed_by_bm: '',
  disbursed_by_staff: '',
  processing_amt: '',
  insurance_amt: '',
  demand_loan_id: '',
  approve_loan_id: '',
  approve_loan_amt: '',
});

const MultipleLoanDisbursement = ({ onShowSingle }) => {
  const { logout } = useAuth();
  const [rows, setRows] = useState([createRow()]);
  const [branches, setBranches] = useState([]);
  const [loans, setLoans] = useState([]);
  const [centersByBranch, setCentersByBranch] = useState({});
  const [clientsByCenter, setClientsByCenter] = useState({});
  const [employeesByBranch, setEmployeesByBranch] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const requestData = useCallback(async (url, body = {}) => {
    const { response, data } = await apiJson(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      logout();
      throw new Error('Your session has expired.');
    }

    if (!response.ok || data?.status === false) {
      throw new Error(data?.message || 'Unable to load form data.');
    }

    return data?.data || [];
  }, [logout]);

  const loadCenters = async (branchId) => {
    if (!branchId || centersByBranch[branchId]) return;
    const data = await requestData(API_CENTERS, { is_all: false, branch_id: branchId });
    setCentersByBranch((current) => ({ ...current, [branchId]: data }));
  };

  const loadClients = async (centerId) => {
    if (!centerId || clientsByCenter[centerId]) return;
    const data = await requestData(API_DISBURSEMENT_CLIENT_HISTORY, { center_id: centerId, status: 'pending' });
    setClientsByCenter((current) => ({ ...current, [centerId]: data }));
  };

  const loadEmployees = async (branchId) => {
    if (!branchId || employeesByBranch[branchId]) return;
    const data = await requestData(API_EMPLOYEES, { branch_id: branchId });
    setEmployeesByBranch((current) => ({ ...current, [branchId]: data }));
  };

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [branchData, loanData] = await Promise.all([
          requestData(API_BRANCHES),
          requestData(API_GET_LOANS, { is_all: true, include_inactive: true }),
        ]);
        setBranches(branchData);
        setLoans(loanData);
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load form data.');
      }
    };

    void loadMasterData();
  }, [requestData]);

  const updateRow = (rowIndex, field, value) => {
    setRows((current) => current.map((row, index) => {
      if (index !== rowIndex) return row;

      if (field === 'branch_id') {
        return {
          ...row,
          branch_id: value,
          center_id: '',
          client_id: '',
          disbursed_by_bm: '',
          disbursed_by_staff: '',
        };
      }

      if (field === 'center_id') {
        return { ...row, center_id: value, client_id: '' };
      }

      if (field === 'client_id') {
        const client = (clientsByCenter[row.center_id] || []).find((item) => String(item.client_id) === String(value));
        return { ...row, client_id: value, demand_loan_id: client?.demand_loan_id || '', approve_loan_id: client?.approve_loan_id || '', approve_loan_amt: client?.approve_loan_amt || '' };
      }

      return { ...row, [field]: value };
    }));

    const loadDependency = async () => {
      try {
        if (field === 'branch_id') {
          await Promise.all([loadCenters(value), loadEmployees(value)]);
        }
        if (field === 'center_id') {
          await loadClients(value);
        }
      } catch (error) {
        setErrorMessage(error.message || 'Unable to load row options.');
      }
    };

    void loadDependency();
  };

  const addRow = () => setRows((current) => [...current, createRow()]);
  const removeRow = (rowIndex) => setRows((current) => current.filter((_, index) => index !== rowIndex));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    if (!confirmAction(`disburse ${rows.length} loan(s)`)) return;

    try {
      setIsSubmitting(true);
      const { response, data } = await apiJson(API_ADD_LOAN_DISBURSE_MULTIPLE, {
        method: 'POST',
        body: JSON.stringify({ disbursements: rows }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      if (!response.ok || !data?.status) {
        throw new Error(data?.message || 'Unable to save loan disbursements.');
      }

      setRows([createRow()]);
      window.alert(data.message);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to save loan disbursements.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container multiple-disbursement-container">
      <div className="disbursement-heading">
        <div>
          <h2>Multiple Loan Disbursement</h2>
          <p>Add one or more independent disbursements. Every row can use different values.</p>
        </div>
        <button type="button" className="btn btn-outline-primary" onClick={onShowSingle}>Single Disbursement</button>
      </div>

      {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}

      <form onSubmit={handleSubmit}>
        <div className="multiple-disbursement-table-wrap">
          <table className="multiple-disbursement-table data-table">
            <thead>
              <tr>
                <th>#</th><th>Branch</th><th>Center</th><th>Client</th><th>Disbursement Date</th><th>Repayment Date</th>
                <th>Branch Manager</th><th>Staff</th><th>Processing</th><th>Insurance</th><th>Demand Loan</th><th>Approved Loan (Auto Disbursed)</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const employees = employeesByBranch[row.branch_id] || [];
                const hasRoles = employees.some((employee) => employee.emp_role !== null && employee.emp_role !== undefined && employee.emp_role !== '');
                const branchManagers = hasRoles ? employees.filter((employee) => String(employee.emp_role) === '1') : employees;
                const staffEmployees = hasRoles ? employees.filter((employee) => String(employee.emp_role) === '2') : employees;

                return (
                  <tr key={rowIndex}>
                    <td>{rowIndex + 1}</td>
                    <td><select value={row.branch_id} onChange={(event) => updateRow(rowIndex, 'branch_id', event.target.value)} required><option value="">Select branch</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}</select></td>
                    <td><select value={row.center_id} onChange={(event) => updateRow(rowIndex, 'center_id', event.target.value)} required disabled={!row.branch_id}><option value="">Select center</option>{(centersByBranch[row.branch_id] || []).map((center) => <option key={center.id} value={center.id}>{center.center_name}</option>)}</select></td>
                    <td><select value={row.client_id} onChange={(event) => updateRow(rowIndex, 'client_id', event.target.value)} required disabled={!row.center_id}><option value="">Select queued client</option>{(clientsByCenter[row.center_id] || []).map((client) => <option key={client.id} value={client.client_id}>{client.client_no} - {client.client_name}</option>)}</select></td>
                    <td><input type="date" value={row.date_of_disbursement} onChange={(event) => updateRow(rowIndex, 'date_of_disbursement', event.target.value)} required /></td>
                    <td><input type="date" value={row.date_of_repayment} onChange={(event) => updateRow(rowIndex, 'date_of_repayment', event.target.value)} required /></td>
                    <td><select value={row.disbursed_by_bm} onChange={(event) => updateRow(rowIndex, 'disbursed_by_bm', event.target.value)} required disabled={!row.branch_id}><option value="">Select manager</option>{branchManagers.map((employee) => <option key={employee.id} value={employee.id}>{employee.emp_generate_id} - {employee.emp_name}</option>)}</select></td>
                    <td><select value={row.disbursed_by_staff} onChange={(event) => updateRow(rowIndex, 'disbursed_by_staff', event.target.value)} required disabled={!row.branch_id}><option value="">Select staff</option>{staffEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.emp_generate_id} - {employee.emp_name}</option>)}</select></td>
                    <td><input type="number" min="0" step="0.01" value={row.processing_amt} onChange={(event) => updateRow(rowIndex, 'processing_amt', event.target.value)} /></td>
                    <td><input type="number" min="0" step="0.01" value={row.insurance_amt} onChange={(event) => updateRow(rowIndex, 'insurance_amt', event.target.value)} /></td>
                    <td><select value={row.demand_loan_id} required disabled><option value="">Queue value</option>{loans.map((loan) => <option key={loan.id} value={loan.id}>{loan.loan_amt}</option>)}</select></td>
                    <td><select value={row.approve_loan_id} required disabled><option value="">Queue value</option>{loans.map((loan) => <option key={loan.id} value={loan.id}>{loan.loan_amt}</option>)}</select></td>
                    <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(rowIndex)} disabled={rows.length === 1}>Remove</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="multiple-disbursement-actions">
          <button type="button" className="btn btn-outline-primary" onClick={addRow}>Add Row</button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : `Save ${rows.length} Disbursement${rows.length === 1 ? '' : 's'}`}</button>
        </div>
      </form>
    </div>
  );
};

export default MultipleLoanDisbursement;
